class ThmvFileDownload {
  constructor() {
    this.modal = null;
    this.controller = null;
    this.closeTimer = null;
    this.currentState = null;
    this.boundClick = this.handleClick.bind(this);
    document.addEventListener('click', this.boundClick);
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-thmv-file-download]');
    if (!trigger) return;

    const url = trigger.getAttribute('href');
    if (!url || url === '#') return;

    event.preventDefault();
    this.startDownload(trigger, url);
  }

  async startDownload(trigger, url) {
    const timeoutSeconds = Number(trigger.dataset.thmvTimeout || 30);
    const timeoutMs = Math.max(timeoutSeconds, 5) * 1000;

    this.clearCloseTimer();
    this.showModal(trigger, 'loading');
    this.controller?.abort();
    this.controller = new AbortController();

    const timeoutId = window.setTimeout(() => this.controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'GET',
        signal: this.controller.signal,
        credentials: 'omit',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = this.getFileName(response, trigger, url);
      this.downloadBlob(blob, fileName);
      this.handleSuccess(trigger);
    } catch (error) {
      this.updateModal('error', trigger, url);
      console.warn('[THMV] Falha ao baixar documento:', error);
    } finally {
      window.clearTimeout(timeoutId);
      this.controller = null;
    }
  }

  getFileName(response, trigger, url) {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    const simpleMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
    if (simpleMatch?.[1]) return simpleMatch[1];

    const configuredName = trigger.dataset.thmvFileName?.trim();
    if (configuredName) {
      const hasExtension = /\.[a-z0-9]{2,8}$/i.test(configuredName);
      return hasExtension ? configuredName : `${configuredName}.pdf`;
    }

    try {
      const parsedUrl = new URL(url, window.location.href);
      const lastPath = parsedUrl.pathname.split('/').filter(Boolean).pop();
      if (lastPath) return decodeURIComponent(lastPath);
    } catch (error) {
      // Keep fallback below.
    }

    return 'documento.pdf';
  }

  downloadBlob(blob, fileName) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 3000);
  }

  handleSuccess(trigger) {
    this.updateModal('success', trigger, trigger.getAttribute('href'));

    const closeDelay = Number(trigger.dataset.thmvSuccessCloseDelay || 1200);
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => {
      this.hideModal();
      this.closeTimer = null;
    }, Math.max(closeDelay, 500));
  }

  showModal(trigger, state) {
    this.ensureModal();
    this.updateModal(state, trigger, trigger.getAttribute('href'));

    if (typeof this.modal.show === 'function') {
      this.modal.show(document.activeElement);
    } else {
      this.modal.setAttribute('open', '');
    }
  }

  hideModal() {
    if (!this.modal) return;
    if (typeof this.modal.hide === 'function') this.modal.hide();
    else this.modal.removeAttribute('open');
  }

  ensureModal() {
    if (this.modal) return;

    // Importante: o atributo data-force precisa existir no elemento no
    // exato momento em que o custom element <modal-dialog> é construído
    // (é isso que o componente do tema checa no construtor). Chamar
    // setAttribute('data-force', ...) depois de document.createElement(...)
    // chega tarde demais — o construtor já rodou sem ver o atributo.
    // Por isso montamos a tag inteira via innerHTML de um wrapper: o parser
    // do navegador aplica os atributos antes de o elemento ser construído.
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <modal-dialog id="THMV-File-Download-Modal" class="thmv-file-download-modal modal" data-force>
        <div class="modal-overlay"></div>
        <div class="modal-inner gradient focus-none" role="dialog" data-trap aria-modal="true" aria-labelledby="THMV-File-Download-Title" tabindex="-1">
          <div class="thmv-file-download-modal__header">
            <b id="THMV-File-Download-Title" class="modal-title font-heading"></b>
          </div>
          <div class="modal-content thmv-file-download-modal__content">
            <div class="thmv-file-download-modal__status" aria-hidden="true">
              <span class="thmv-file-download-modal__spinner">
                <span class="thmv-file-download-modal__spinner-dot"></span>
              </span>
              <span class="thmv-file-download-modal__icon"></span>
            </div>
            <div class="thmv-file-download-modal__message"></div>
            <a class="thmv-file-download-modal__fallback button button--small button--pill" href="#" target="_blank" rel="noopener" hidden></a>
          </div>
        </div>
      </modal-dialog>
    `;
    const modal = wrapper.firstElementChild;

    modal.addEventListener('click', (event) => {
      if (!event.target.classList.contains('modal-overlay')) return;
      if (this.currentState === 'loading') return;
      modal.hide();
    });

    modal.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      if (this.currentState === 'loading') return;
      modal.hide();
    });

    document.body.appendChild(modal);
    this.modal = modal;
  }

  updateModal(state, trigger, url) {
    if (!this.modal) return;

    this.currentState = state;

    const title = trigger.dataset.thmvModalTitle || 'Gerando ficha técnica';
    const loadingText = trigger.dataset.thmvLoadingText || 'Estamos preparando sua ficha técnica. Isso pode levar alguns segundos.';
    const loadingNote = trigger.dataset.thmvLoadingNote || 'O download será iniciado automaticamente.';
    const successText = trigger.dataset.thmvSuccessText || 'Ficha técnica gerada com sucesso';
    let successDetail = trigger.dataset.thmvSuccessDetail || 'Sua ficha técnica foi gerada e baixada com sucesso.';
    if (successDetail === 'A ficha técnica foi gerada com sucesso e o download foi concluído.') {
      successDetail = 'Sua ficha técnica foi gerada e baixada com sucesso.';
    }
    const errorTitle = trigger.dataset.thmvErrorTitle || 'Não foi possível gerar';
    const errorText = trigger.dataset.thmvErrorText || 'Não foi possível gerar a ficha técnica agora. Tente novamente em alguns instantes.';
    const buttonText = trigger.dataset.thmvButtonText || 'Abrir documento';

    const titleEl = this.modal.querySelector('#THMV-File-Download-Title');
    const contentEl = this.modal.querySelector('.thmv-file-download-modal__content');
    const messageEl = this.modal.querySelector('.thmv-file-download-modal__message');
    const fallbackEl = this.modal.querySelector('.thmv-file-download-modal__fallback');

    contentEl.dataset.state = state;
    this.modal.dataset.state = state;

    if (state === 'loading') {
      titleEl.textContent = title;
      messageEl.innerHTML = this.buildMessage(loadingText, loadingNote);
      fallbackEl.hidden = true;
      fallbackEl.href = '#';
    } else if (state === 'success') {
      titleEl.textContent = successText;
      messageEl.textContent = successDetail;
      fallbackEl.hidden = true;
      fallbackEl.href = '#';
    } else {
      titleEl.textContent = errorTitle;
      messageEl.textContent = errorText;
      fallbackEl.textContent = buttonText;
      fallbackEl.href = url;
      fallbackEl.hidden = false;
    }
  }

  buildMessage(primary, note) {
    const safePrimary = this.escapeHtml(primary || '');
    const safeNote = this.escapeHtml(note || '');

    if (!safeNote) return safePrimary;

    return `${safePrimary}<small>${safeNote}</small>`;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

if (!window.thmvFileDownload) {
  window.thmvFileDownload = new ThmvFileDownload();
}
